<template>
  <v-container>
    <v-row>
      <v-col align="center" justify="center">
        <v-card width="900px">
          <v-row>
            <v-col cols="4" class="pr-0">
              <v-img v-if="product.logoPath" style="max-width: 300px; width: 300px; max-height: 150px; height: 150px" :src="product.logoPath" contain></v-img>
              <v-icon v-else large>error_outline</v-icon>
            </v-col>
            <v-col cols="8">
              <v-card-title class="pt-0">
                <v-row>
                  <v-col cols="11" class="text-left">
                    {{ product.name }}
                  </v-col>
                  <v-col v-if="product.helpContentUrl" cols="1" class="text-right">
                    <v-btn icon :href="product.helpContentUrl" target="_blank">
                      <v-icon color="#145392">help_outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="text--primary text-justify" v-html="product.description"></div>
                <div v-if="product.minimumCmsVersion" class="text-justify font-italic pt-2">
                  This product requires HawkSoft CMS Version {{ product.minimumCmsVersion }}.
                </div>
                <v-form v-if="product.requiresOptIn" class="pt-3 text-justify">
                  <div class="ma-0">
                    By checking the box below, I acknowledge that I have read and agree to the terms outlined in the
                    <a target="_blank" :href="product.termsAndConditionsUrl">{{ product.termsAndConditionsLabel }}</a>, and consent to sync my data.
                  </div>
                  <div class="pt-3">
                    <v-checkbox v-model="dataOptIn"
                                :disabled="isOptedIntoDataSharing">
                      <template slot="label">
                        <div class="pl-3">
                          <v-row>{{ termsAndConditionsLabel }}</v-row>
                          <v-row v-if="isOptedIntoDataSharing">{{ getOptInText }}</v-row>
                        </div>
                      </template>
                    </v-checkbox>
                  </div>
                </v-form>
                <LeavingDialog :show-leaving-dialog="showLeavingDialog" :url="activeButtonUrl" @close="showLeavingDialog = false" />
              </v-card-text>
              <v-card-actions>
                <v-container>
                  <v-row>
                    <v-col v-if="product.requiresHawkSoftCloud && !product.isHawkSoftCloud && !hawkSoftCloudEnabled" class="error--text body-2">
                      In order to use this feature, you must first enable HawkSoft Data Sync.
                    </v-col>
                    <v-col v-if="showDataSyncV5Message" class="error--text body-2">
                      In order to use this feature, you must first upgrade to Version 5.0 or greater
                    </v-col>

                  </v-row>
                  <v-row>
                    <v-col cols="auto" class="text-left">
                      <v-btn :color="product.isSubscribed ? 'primary' : 'warning'"
                             :disabled="!formValid"
                             @click="submit">
                        {{ activeButtonLabel }}
                      </v-btn>
                    </v-col>
                    <v-spacer />
                    <v-col v-if="product.requiresOptIn && product.providerName != 'HawkSoft'" cols="auto" class="text-right">
                      <v-btn :class="{ 'primary' : !product.isSubscribed, 'ml-auto' : true } "
                             :disabled="!formValid"
                             @click="toggleSubscription">
                        {{ product.isSubscribed ? 'Stop Sharing Data' : 'Share Data' }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import Moment from 'moment';
import LeavingDialog from './LeavingDialog.vue';

export default {
  name: 'ProductInformation',
  components: {
    LeavingDialog
  },
  props: {
    productId: {
      type: Number, required: true
    }
  },
  data () {
    return {
      dataOptIn: false,
      showLeavingDialog: false
    };
  },
  computed: {
    ...mapGetters(['getProductById', 'hasVersion5']),
    ...mapState(['hawkSoftCloudEnabled', 'agencyUser']),
    product () {
      return this.getProductById(this.productId);
    },
    termsAndConditionsLabel () {
      return 'I have read and accept the ' + this.product.termsAndConditionsLabel + '.';
    },
    getOptInText () {
      // eslint-disable-next-line new-cap
      var optInDate = new Moment(this.product.dataSharingOptIn.optInDate).format('ddd, MMM D YYYY, h:mm:ss a');
      return `Accepted by: ${this.product.dataSharingOptIn.agencyUserName}, ${optInDate}`;
    },
    isOptedIntoDataSharing () {
      return this.product.dataSharingOptIn !== null;
    },
    activeButtonLabel () {
      return this.product.isSubscribed ? this.product.manageButtonLabel : this.product.actionButtonLabel;
    },
    activeButtonUrl () {
      return this.product.isSubscribed ? this.product.manageButtonUrl : this.product.actionButtonUrl;
    },
    formValid () {
      if (this.product.requiresHawkSoftCloud && !this.hawkSoftCloudEnabled && !this.product.isHawkSoftCloud) {
        return false;
      }
      if (this.product.requiresOptIn && !this.dataOptIn) {
        return false;
      }
      if (this.product.isHawkSoftCloud) {
        if (this.hawkSoftCloudEnabled) return true;
        if (this.hasVersion5) return true;
        return false;
      }
      return true;
    },
    showDataSyncV5Message () {
      if (!this.product.isHawkSoftCloud) return false;
      if (this.hawkSoftCloudEnabled) return false;
      if (this.hasVersion5) return false;
      return true;
    }
  },
  mounted () {
    this.dataOptIn = this.product.dataSharingOptIn !== null;
  },
  methods: {
    ...mapActions(['setProductSubscription', 'setDataSharingOptIn', 'setError']),
    async submit () {
      if (this.product.requiresOptIn && !this.product.dataSharingOptIn) {
        const resp = await this.$api.acceptDataShareTermsAndConditions(this.productId);
        if (resp.code !== 0 && resp.code !== 2) {
          this.setError({ errorMessage: `Error - Unable to opt-in to data sync. Message: ${resp.message}` });
          return;
        }
        this.setDataSharingOptIn({ productId: this.productId, optInModel: JSON.parse(resp.data[0]) });
      }
      await this.sendNotificationEmails();
    },
    async sendNotificationEmails () {
      if (!this.product.isSubscribed) {
        await this.$api.sentInterestNotifications(this.productId);
      }
      this.navigateToProductUrl();
    },
    navigateToProductUrl () {
      if (!this.product.isSubscribed && this.product.requiresOptIn && this.product.providerName !== 'HawkSoft') {
        this.showLeavingDialog = true;
      } else if (this.activeButtonUrl.startsWith('http')) {
        window.location.href = this.activeButtonUrl;
      } else {
        this.$router.push({ path: this.activeButtonUrl });
      }
    },
    async toggleSubscription () {
      const response = await this.$api.setSubscriptionDetailed(this.productId, !this.product.isSubscribed);
      if (response.code !== 0 && response.code !== 2) {
        this.setError({ errorMessage: `Error - Unable to set subscription. Message: ${response.message}` });
        return;
      }
      this.setProductSubscription({ productId: this.productId, subscription: !this.product.isSubscribed });
    }
  }
};

</script>
